<template>
    <div>
        <div class="header">Résumé des informations</div>

        <v-row dense>
            <v-col>
                <v-card outlined class="card">
                    <v-card-title class="pt-1 pb-2"> Type d'offre choisi: </v-card-title>
                    <v-card-text class="pb-1">
                        <p class="mb-1">{{ offerType }}</p>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <div style="overflow-y: auto; overflow-x: hidden; max-height: 50vh">
            <v-row dense>
                <v-col class="pb-0">
                    <v-card outlined class="card pb-0">
                        <v-card-title class="pt-1 pb-2"> Informations personnelles: </v-card-title>
                        <v-card-text class="pb-0">
                            <p class="mb-1">Nom: {{ data.user.lastname }}</p>
                            <p class="mb-1">Prenom: {{ data.user.name }}</p>
                            <p class="mb-1">Email: {{ data.user.email }}</p>
                            <p class="mb-0">Numéro de téléphone: {{ data.user.phoneNumber }}</p>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col class="pb-0">
                    <v-card outlined class="card pb-0">
                        <v-card-title class="pt-1 pb-2">
                            Informations de l'entreprise:
                        </v-card-title>
                        <v-card-text class="pb-0">
                            <p class="mb-1" v-if="data.company.name">
                                Nom de l'entreprise: {{ data.company.name }}
                            </p>
                            <p class="mb-1" v-if="data.company.legalUnitName">
                                Nom légale: {{ data.company.legalUnitName }}
                            </p>
                            <p class="mb-1" v-if="data.company.tradeName">
                                Nom commercial: {{ data.company.tradeName }}
                            </p>
                            <p class="mb-1">Siret: {{ data.company.siret }}</p>
                            <p class="mb-1">Siren: {{ data.company.siren }}</p>
                            <p class="mb-1">Numéro de TVA: {{ data.company.vatNumber }}</p>
                            <p class="mb-1">
                                Adresse: {{ data.company.address }} {{ data.company.city }},
                                {{ data.company.postalCode }} {{ data.company.country }}
                            </p>
                            <p class="mb-0" v-if="data.company.legalForm">
                                {{ $t("settings.legalForm") }}: {{ data.company.legalForm }}
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col class="pb-0">
                    <v-card outlined class="card pb-0">
                        <v-card-title class="pt-1 pb-2"> Informations comptables: </v-card-title>
                        <v-card-text class="pb-0">
                            <p class="mb-1">Catégorie de revenu: {{ incomeCategory }}</p>
                            <p class="mb-1">Régime fiscal: {{ taxRegime }}</p>
                            <p class="mb-1">Type de comptabilité: {{ accountingType }}</p>
                            <p class="mb-0">
                                &#xca;tes-vous assujetti à la TVA:
                                {{ data.accounting.payedVAT ? "oui" : "non" }}
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col class="pb-0">
                    <v-card outlined class="card pb-0">
                        <v-card-title class="pt-1 pb-2"> Informations déclaratives: </v-card-title>
                        <v-card-text class="pb-0">
                            <p class="mb-1">Régime TVA: {{ vatSystems }}</p>
                            <p class="mb-1">
                                Jour déclaration TVA: {{ data.declaration.vatDeclarationDay }}
                            </p>
                            <p class="mb-1">Option de TVA: {{ vatOptions }}</p>
                            <p class="mb-1">Imposition: {{ taxSystemTypes }}</p>
                            <p class="mb-1" v-if="data.declaration.startBusinessDate">
                                Date de début d'activité: {{ startDate }}
                            </p>
                            <p class="mb-0" v-if="data.declaration.firstAccountingPeriodEnd">
                                Date de fin du premier exercice: {{ endDate }}
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";

import { getVATSystem } from "@/services/vat/vatHelpers.services";
import { Offer_type } from "@/types/offer.types";
import { registrationInfo } from "@/types/registerServices.types";
import { Component, Model, Vue, toNative } from "vue-facing-decorator";

@Component
export class ResumePage extends Vue {
    @Model({ default: null }) readonly data: registrationInfo;

    get offerType() {
        switch (this.data.offer) {
            case Offer_type.BUSINESS_BASIC:
                return "Offre FREEMIUM";
            case Offer_type.BUSINESS_SMART:
                return "Offre SMART";
            case Offer_type.BUSINESS_ESSENTIAL:
                return "Offre ESSENTIEL";
            case Offer_type.BUSINESS_PREMIUM:
                return "Offre PREMIUM";
        }
    }

    get incomeCategory() {
        return this.data.accounting.incomeCategory;
    }

    get taxRegime() {
        switch (this.data.accounting.taxRegime) {
            case "OTHER":
                return "Autre";
            case "SELF_EMPLOYED":
                return "Auto-entrepreneur";
            case "CONTROLLED_DECLARATION":
                return "Déclaration contrôlée";
            case "MICRO_BA":
                return "Micro BA";
            case "MICRO_ENTERPRISE":
                return "Micro entreprise";
            case "REAL_SIMPLIFIED":
                return "Réel simplifié";
            case "REAL_NORMAL":
                return "Réel normal";
            default:
                return "Inconnu";
        }
    }

    get accountingType() {
        switch (this.data.accounting.accountingType) {
            case "RECEIVABLES_DEBT":
                return "Comptabilité d'engagement";
            case "INCOMING_OUTGOING_PAYMENT":
                return "Comptabilité de trésorerie";
        }
    }

    get vatSystems() {
        return getVATSystem(this.data.declaration.vatSystem);
    }

    get vatOptions() {
        switch (this.data.declaration.vatOption) {
            case null:
                return "N/C";
            case "Debits":
                return "Débits";
            case "Receipts":
                return "Encaissements";
        }
    }

    get taxSystemTypes() {
        switch (this.data.declaration.taxSystemType) {
            case "IS":
                return "Impôts sur les Sociétés";
            case "IR":
                return "Impôts sur les Revenus";
        }
    }

    get startDate() {
        return moment(this.data.declaration.startBusinessDate).format("DD/MM/YYYY");
    }

    get endDate() {
        return moment(this.data.declaration.firstAccountingPeriodEnd).format("DD/MM/YYYY");
    }
}

export default toNative(ResumePage);
</script>

<style scoped>
.header {
    text-align: center;
    margin-bottom: 2vh;
    font-weight: bold;
}

.card {
    border-width: 0px;
}

::-webkit-scrollbar {
    width: 5px;
}
::-webkit-scrollbar-track {
    background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
    background: #888;
}
</style>
